"use client";

import { useEffect, useState } from "react";
import { useUbStorefrontState } from "@/store/ubStorefrontState";
import { sendGTMEvent } from "@next/third-parties/google";



import { generatePageTypeTrackingData } from "@/lib/gtmTrackingHelper";
import { CardPrices, determineCardPrices } from "@/lib/price-utils";
import { PdpContext } from "@/lib/storefront/product/pdp-service";
import { calculateCategoryPathString, determineEffectiveProductFromPdpContext, determineVariableAttributeValuesAsString } from "@/lib/storefront/product/productHelper";
import { buildGa4Item, resetEcommerce, sendGTMEventIfAnalyticsAllowed, toCustomerType } from "@/lib/ga4TrackingHelper";
import { consumeListingAttributionForProduct } from "@/lib/tracking/listing-attribution";
import { usePdpDynamicContext } from "@/components/product/pdp/usePdpDynamicContext";





export interface PdpPageTrackingClientProps {
  pdpContext: PdpContext
}

export default function PdpPageTrackingClient(props: PdpPageTrackingClientProps) {
  const [tracked, setTracked] = useState(false)
  const { usedGuidedSelling, guidedSellingCampaign } = useUbStorefrontState()
  const pdpContext = usePdpDynamicContext(props.pdpContext)

  useEffect(() => {
    if (!tracked) {
      if (pdpContext.pricesLoading) {
        return
      }

      const productOrArticle = determineEffectiveProductFromPdpContext(pdpContext)

      // UA page type event remains for custom dimension; GA4 event follows
      sendGTMEvent(
        generatePageTypeTrackingData(
          pdpContext.isAnonymous,
          pdpContext.isB2B,
          pdpContext.productAndArticle.masterProduct && !pdpContext.productAndArticle.article
            ? "PDP_Master"
            : "PDP_Article",
          pdpContext?.userData?.customerNo,
          undefined,
          undefined,
          usedGuidedSelling,
          guidedSellingCampaign
        )
      )
      // GA4 view_item with gross value
      const cardPricesGross: CardPrices = determineCardPrices(
        pdpContext.isoLocale,
        /*isB2BCustomer*/ false,
        pdpContext.prices,
        productOrArticle?.ecoTax
      )

      const categoryPathString: string | undefined = calculateCategoryPathString(productOrArticle)
      const customer_type = toCustomerType(pdpContext.isAnonymous, pdpContext.isB2B)
      const pagetyp = pdpContext.productAndArticle.masterProduct && !pdpContext.productAndArticle.article
        ? "pdp_master"
        : "pdp_artikel"
      const listingAttribution = consumeListingAttributionForProduct([
        pdpContext.productId,
        productOrArticle?.id,
        pdpContext.productAndArticle.article?.id,
        pdpContext.productAndArticle.masterProduct?.id,
      ])

      sendGTMEventIfAnalyticsAllowed(resetEcommerce())
      sendGTMEventIfAnalyticsAllowed({
        event: "view_item",
        pagetyp,
        customer_type,
        ecommerce: {
          currency: cardPricesGross.currency,
          value: cardPricesGross.salePrice,
          items: [
            buildGa4Item({
              product: productOrArticle,
              priceGross: cardPricesGross.salePrice,
              currency: cardPricesGross.currency,
              variant: determineVariableAttributeValuesAsString(productOrArticle),
              categoryPath: categoryPathString,
              item_list_id: listingAttribution?.item_list_id,
              item_list_name: listingAttribution?.item_list_name,
            }),
          ],
        },
      })
      sendGTMEventIfAnalyticsAllowed(resetEcommerce())
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setTracked(true)
    }
  }, [pdpContext, tracked, usedGuidedSelling, guidedSellingCampaign])

  return <span className={"hidden"}>CategoryBrandsSectionTrackingClient</span>
}

// UA detailView removed in favor of GA4 view_item

//TODO - Beipiel Artikel
/*
{
  "event": "detailView",
  "ecommerce": {
  "currencyCode": "EUR",
    "impressions": [],
    "promoView": {
    "promotions": []
  },
  "detail": {
    "actionField": {
      "list": "Büroeinrichtung/Schreibtische und Tische/elektrisch höhenverstellbare Schreibtische",
        "action": "detail"
    },
    "products": [
      {
        "name": "Topstar Elektrisch höhenverstellbarer Schreibtisch E-Table Smart mit T-Fußgestell",
        "id": "A254327",
        "price": "463.5",
        "brand": "Topstar",
        "variant": "1200 mm/600 mm/weiß/weiß/",
        "category": "Büroeinrichtung/Schreibtische und Tische/elektrisch höhenverstellbare Schreibtische"
      }
    ]
  }
},
  "gtm.uniqueEventId": 79
}*/
//TODO - Beispiel MasterData

/*
{
  "event": "detailView",
  "ecommerce": {
  "currencyCode": "EUR",
    "impressions": [],
    "promoView": {
    "promotions": []
  },
  "detail": {
    "actionField": {
      "list": "Büroeinrichtung/Schreibtische und Tische/elektrisch höhenverstellbare Schreibtische"
    },
    "products": [
      {
        "name": "Topstar Elektrisch höhenverstellbarer Schreibtisch E-Table Smart",
        "id": "E22495",
        "price": "463.5",
        "brand": "Topstar",
        "category": "Büroeinrichtung/Schreibtische und Tische/elektrisch höhenverstellbare Schreibtische"
      }
    ]
  }
},
  "gtm.uniqueEventId": 224
}*/
